import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Trash2,
  CircleDot,
  Hammer,
  ClipboardCheck,
  CalendarClock,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import { fmtCurrency } from "../../lib/display-labels";
import { formatInstantDay } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty, DhPanel } from "../downhole/DownholeBits";
import MaintenanceCostReport from "../../components/rentals/MaintenanceCostReport";
import {
  DASH,
  PRIORITY,
  Skin,
  StatusBadge,
  UNKNOWN,
  WO_STATUS,
  WorkOrderApiRow,
  normalizeWorkOrder,
  numOrUndefined,
  priorityLabel,
  woSkin,
} from "./rental-work-order";

/**
 * Rental maintenance, /rentals/maintenance. Full width, the layout of the
 * rental Field Tickets register: band, tabs (Work Orders / Schedules / Cost
 * report), status pills, search, one register table; a row opens
 * /rentals/maintenance/:id.
 *
 * It was a split pane (a 380px list beside a detail panel) until #815. The
 * work order is now its own page (RentalWorkOrderDetail), and the shared
 * shape, normalizer and skins live in rental-work-order.tsx.
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * A schedule as `GET /surface-rentals/maintenance/schedules` sends it:
 * `maintenance_schedules.*` plus `asset_name`, `asset_number`, `asset_type` and
 * `due_soon`. There is no `status` column. This page used to read `status`,
 * `asset_tag`, `last_performed` and `next_due`, none of which the API sends.
 */
interface ScheduleApiRow {
  id: string;
  asset_name?: string | null;
  asset_number?: string | null;
  trigger_type?: string | null;
  trigger_value?: string | number | null;
  last_performed_at?: string | null;
  next_due_at?: string | null;
  /** The API's own `next_due_at < now() + 7 days`. A null next_due_at is false. */
  due_soon?: boolean | null;
}

interface AssetOption {
  id: string;
  name: string;
  tag: string | null;
}

interface CrewMember {
  id: string;
  name: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * A schedule's state, from the API's own `due_soon` and nothing computed here.
 * `due_soon` covers "within seven days" AND "already past", so it is not split
 * into Overdue client-side — that would be a second answer to the API's
 * question. `false` is only "Scheduled" when there IS a due date: the API
 * returns false for a null `next_due_at` too, and no due date is a gap, not a
 * clean bill of health.
 */
function scheduleSkin(s: ScheduleApiRow): Skin {
  if (s.due_soon === true) return { label: "Due Soon", fill: "bg-fi-signal-amber", rail: "border-l-fi-amber" };
  if (s.due_soon === false && s.next_due_at) return { label: "Scheduled", fill: "bg-fi-signal-green", rail: "border-l-fi-green" };
  return UNKNOWN;
}

/**
 * What the New Schedule dialog offers: three of the CHECK's four values.
 * `calendar` is left out because nothing says how it differs from `days`;
 * existing `calendar` rows still render through TRIGGER_TYPE below.
 */
const SCHEDULE_TRIGGERS = [
  { value: "hours", label: "Engine hours", unit: "hours" },
  { value: "days", label: "Days", unit: "days" },
  { value: "odometer", label: "Odometer", unit: "km" },
] as const;

/** Exhaustive over `maintenance_schedules_trigger_type_check`. */
const TRIGGER_TYPE: Record<string, string> = {
  hours: "Hours",
  days: "Days",
  calendar: "Calendar",
  odometer: "Odometer",
};

const statusFilters = ["all", "open", "in_progress", "pending_approval", "approved", "cancelled"] as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const MaintenanceWorkOrders = () => {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();
  const [activeView, setActiveView] = useState<"work_orders" | "schedules" | "cost_report">("work_orders");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  /* ── Reads. One query per source, so each can fail on its own ── */
  // The four used to run through one Promise.allSettled whose rejections were
  // dropped, so a failed load of work orders rendered "No work orders found".
  const workOrdersQuery = useQuery({
    // The zone is in the key because the rows carry a day formatted in it.
    queryKey: ["rentals", "maintenance", "work-orders", timeZone],
    queryFn: () =>
      api.rentals.maintenance.workOrders.list().then((r) =>
        unwrapArray<WorkOrderApiRow>(r.data).map((row) => normalizeWorkOrder(row, timeZone))),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const schedulesQuery = useQuery({
    queryKey: ["rentals", "maintenance", "schedules"],
    queryFn: () => api.rentals.maintenance.schedules.list().then((r) => unwrapArray<ScheduleApiRow>(r.data)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  // Dialog options only. The asset registry sends `asset_number`; `tag` has never been a column.
  const assetsQuery = useQuery({
    queryKey: ["rentals", "maintenance", "asset-options"],
    queryFn: () =>
      api.rentals.assets.list().then((r) =>
        unwrapArray<{ id: string; name?: string | null; asset_number?: string | null }>(r.data).map(
          (a): AssetOption => ({ id: a.id, name: a.name ?? DASH, tag: a.asset_number ?? null }),
        ),
      ),
    staleTime: 30_000,
  });
  const usersQuery = useQuery({
    queryKey: ["rentals", "maintenance", "crew-options"],
    queryFn: () =>
      api.users.list().then((r) =>
        unwrapArray<{ id: string; first_name?: string | null; last_name?: string | null; email?: string | null }>(r.data).map(
          (u): CrewMember => ({
            id: u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || DASH,
          }),
        ),
      ),
    staleTime: 30_000,
  });

  const woFailed = queryLoadFailure(workOrdersQuery) != null;
  const schedFailed = queryLoadFailure(schedulesQuery) != null;
  const woLoading = workOrdersQuery.isLoading && !woFailed;
  const schedLoading = schedulesQuery.isLoading && !schedFailed;
  const workOrders = workOrdersQuery.data;
  const schedules = schedulesQuery.data;
  const assetOptions = assetsQuery.data ?? [];
  const crewOptions = usersQuery.data ?? [];

  const refreshMaintenance = () => queryClient.invalidateQueries({ queryKey: ["rentals", "maintenance"] });

  // New Work Order dialog
  const [newWOOpen, setNewWOOpen] = useState(false);
  const [formAsset, setFormAsset] = useState("");
  const [formPriority, setFormPriority] = useState<string>("medium");
  const [formDescription, setFormDescription] = useState("");
  const [formAssignTo, setFormAssignTo] = useState("");
  const [formChecklist, setFormChecklist] = useState<string[]>([""]);

  const [creatingWO, setCreatingWO] = useState(false);
  const [creatingSchedule, setCreatingSchedule] = useState(false);

  // New Schedule dialog
  const [newScheduleOpen, setNewScheduleOpen] = useState(false);
  const [schedAsset, setSchedAsset] = useState("");
  const [schedTriggerType, setSchedTriggerType] = useState("");
  const [schedTriggerValue, setSchedTriggerValue] = useState("");

  /* ── Filtered data ── */
  const filteredWOs = useMemo(
    () =>
      (workOrders ?? []).filter((wo) => {
        if (statusFilter !== "all" && wo.status !== statusFilter) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          return (
            (wo.wo_number ?? "").toLowerCase().includes(q) ||
            (wo.asset_name ?? "").toLowerCase().includes(q) ||
            (wo.assigned_to ?? "").toLowerCase().includes(q)
          );
        }
        return true;
      }),
    [workOrders, statusFilter, searchQuery],
  );

  /**
   * Chip and band counts are tallies of the loaded list. Honest here: GET
   * /maintenance/work-orders is unpaginated (no LIMIT) and no endpoint returns
   * these figures.
   */
  const countOf = (status: string): number | null =>
    workOrders ? workOrders.filter((w) => status === "all" || w.status === status).length : null;
  const dueSoon = schedules ? schedules.filter((s) => s.due_soon === true).length : null;

  /* ── Actions ── */
  const openWorkOrder = (id: string) => navigate(`/rentals/maintenance/${encodeURIComponent(id)}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openWorkOrder(id);
    }
  };

  const handleCreateWO = async () => {
    if (!formAsset || !formDescription) {
      toast.error("Asset and description are required");
      return;
    }
    const data = {
      asset_id: formAsset,
      priority: formPriority,
      description: formDescription,
      // `assigned_to` is a uuid column. An unset picker holds "", which
      // `firstUnownedField` skips as absent and the INSERT then fed straight to
      // Postgres — 22P02, and a 500 on every work order created without an
      // assignee. `?? null` does not catch it: "" is neither null nor undefined.
      assigned_to: formAssignTo || undefined,
      // The handler reads `checklist_json`. Sent as `checklist` it was dropped
      // silently: the work order was created, the row stored the default '[]',
      // and the checklist the user typed was gone with no error anywhere.
      checklist_json: formChecklist.filter(Boolean).map((label, i) => ({
        id: `c${i + 1}`,
        label,
        completed: false,
      })),
    };
    setCreatingWO(true);
    try {
      await api.rentals.maintenance.workOrders.create(data);
      await refreshMaintenance();
      toast.success("Work order created");
      setNewWOOpen(false);
      setFormAsset("");
      setFormPriority("medium");
      setFormDescription("");
      setFormAssignTo("");
      setFormChecklist([""]);
    } catch (err) {
      // The dialog stays open with everything the user typed still in it.
      // Closing and resetting on failure threw the work away as well as the
      // work order.
      toast.error(getApiErrorMessage(err, "Failed to create work order"));
    } finally {
      setCreatingWO(false);
    }
  };

  /**
   * `trigger_type` must be one of `maintenance_schedules_trigger_type_check`
   * and `trigger_value` is `numeric NOT NULL` (#815). The dialog offered
   * "Hours" / "Calendar" / "Cycles" (capitalised, and Cycles has no
   * equivalent) with a free-text value like "500 hrs", so every create
   * failed. It now sends a type from the CHECK and a positive number.
   */
  const handleCreateSchedule = async () => {
    const value = Number(schedTriggerValue);
    if (!schedAsset || !schedTriggerType || schedTriggerValue.trim() === "") {
      toast.error("All fields are required");
      return;
    }
    if (!Number.isFinite(value) || value <= 0) {
      toast.error("Every must be a number greater than 0.");
      return;
    }
    const data = {
      asset_id: schedAsset,
      trigger_type: schedTriggerType,
      trigger_value: value,
    };
    setCreatingSchedule(true);
    try {
      await api.rentals.maintenance.schedules.create(data);
      await refreshMaintenance();
      toast.success("Maintenance schedule created");
      setNewScheduleOpen(false);
      setSchedAsset("");
      setSchedTriggerType("");
      setSchedTriggerValue("");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to create maintenance schedule"));
    } finally {
      setCreatingSchedule(false);
    }
  };

  const tabClass = (active: boolean) =>
    `-mb-px border-b-2 px-1 pb-2 text-sm font-extrabold transition-colors ${
      active ? "border-fi-amber text-fieldiq-text" : "border-transparent text-fieldiq-muted hover:text-fieldiq-text"
    }`;

  /* ── Render ── */
  return (
    <div data-testid="maintenance-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Maintenance"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          activeView === "cost_report" ? undefined : (
          <button
            type="button"
            onClick={() => (activeView === "work_orders" ? setNewWOOpen(true) : setNewScheduleOpen(true))}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" />
            {activeView === "work_orders" ? "New Work Order" : "Add Schedule"}
          </button>
          )
        }
        stats={[
          {
            icon: <CircleDot className="size-4" aria-hidden="true" />,
            label: "Open",
            sub: "Not started",
            tone: "info",
            loading: woLoading,
            value: woFailed ? DASH : displayValue(countOf("open")),
          },
          {
            icon: <Hammer className="size-4" aria-hidden="true" />,
            label: "In Progress",
            sub: "Work under way",
            tone: "accent",
            loading: woLoading,
            value: woFailed ? DASH : displayValue(countOf("in_progress")),
          },
          {
            icon: <ClipboardCheck className="size-4" aria-hidden="true" />,
            label: "Pending",
            sub: "To approve",
            tone: "neutral",
            loading: woLoading,
            value: woFailed ? DASH : displayValue(countOf("pending_approval")),
          },
          {
            icon: <CalendarClock className="size-4" aria-hidden="true" />,
            label: "Due Soon",
            sub: "Next 7 days",
            tone: "accent",
            loading: schedLoading,
            value: schedFailed ? DASH : displayValue(dueSoon),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Tabs, not chips: the three views answer different questions from
              different lists, so no tab carries a count. */}
          <div role="tablist" aria-label="Maintenance views" className="flex gap-5 border-b border-fieldiq-border">
            <button
              type="button"
              role="tab"
              aria-selected={activeView === "work_orders"}
              onClick={() => setActiveView("work_orders")}
              className={tabClass(activeView === "work_orders")}
            >
              Work Orders
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeView === "schedules"}
              onClick={() => setActiveView("schedules")}
              className={tabClass(activeView === "schedules")}
            >
              Schedules
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeView === "cost_report"}
              onClick={() => setActiveView("cost_report")}
              className={tabClass(activeView === "cost_report")}
            >
              Cost report
            </button>
          </div>

          {activeView === "work_orders" ? (
            <>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
                  {statusFilters.map((f) => {
                    const active = statusFilter === f;
                    const n = countOf(f);
                    return (
                      <button
                        key={f}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setStatusFilter(f)}
                        className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                          active
                            ? "bg-fi-amber text-fi-amber-ink"
                            : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                        }`}
                      >
                        {f === "all" ? "All" : WO_STATUS[f].label}
                        {!woLoading && !woFailed && n != null && (
                          <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{n}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="relative lg:w-80">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Search work orders..."
                    aria-label="Search work orders"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                  />
                </div>
              </div>

              <DhPanel
                eyebrow="Work orders"
                title="Rental asset repairs"
                testId="wo-register"
                footer={
                  woFailed || woLoading || !workOrders || workOrders.length === 0
                    ? undefined
                    : `Showing ${filteredWOs.length} of ${workOrders.length} work orders`
                }
              >
                {woFailed ? (
                  <LoadFailureNotice error={queryFailureError(workOrdersQuery)} subject="work orders" />
                ) : woLoading ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading work orders" />
                  </div>
                ) : filteredWOs.length === 0 ? (
                  <DhEmpty>
                    {workOrders && workOrders.length === 0 ? "No work orders yet" : "No work orders match these filters"}
                  </DhEmpty>
                ) : (
                  <>
                    <div className="hidden overflow-x-auto md:block">
                      <table className="w-full min-w-[980px] text-left">
                        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                          <tr className="border-l-4 border-l-transparent">
                            <th scope="col" className="whitespace-nowrap px-4 py-3">WO #</th>
                            <th scope="col" className="px-4 py-3">Asset</th>
                            <th scope="col" className="px-4 py-3">Priority</th>
                            <th scope="col" className="px-4 py-3">Status</th>
                            <th scope="col" className="px-4 py-3">Opened</th>
                            <th scope="col" className="px-4 py-3">Assigned</th>
                            <th scope="col" className="px-4 py-3 text-right">Total cost</th>
                            <th scope="col" className="w-[44px] px-4 py-3" />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredWOs.map((wo) => {
                            const skin = woSkin(wo.status);
                            const pr = wo.priority ? PRIORITY[wo.priority] : undefined;
                            return (
                              <tr
                                key={wo.id}
                                data-testid="wo-row"
                                role="button"
                                tabIndex={0}
                                onClick={() => openWorkOrder(wo.id)}
                                onKeyDown={rowKeys(wo.id)}
                                className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skin.rail}`}
                              >
                                <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-extrabold text-fieldiq-accent-signal">{wo.wo_number}</td>
                                <td className="min-w-[12rem] px-4 py-3">
                                  <p className="text-sm font-bold text-fieldiq-text">{wo.asset_name}</p>
                                  <p className="font-mono text-[11px] text-fieldiq-muted">{wo.asset_tag}</p>
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                                  <span className="inline-flex items-center gap-2">
                                    {pr && <span aria-hidden="true" className={`h-2 w-2 rounded-full ${pr.dot}`} />}
                                    {priorityLabel(wo.priority)}
                                  </span>
                                </td>
                                <td className="px-4 py-3"><StatusBadge skin={skin} /></td>
                                <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{wo.opened_date}</td>
                                <td className="min-w-[10rem] px-4 py-3 text-sm text-fieldiq-text">{wo.assigned_to}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm font-extrabold text-white">{fmtCurrency(wo.total_cost)}</td>
                                <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="space-y-3 p-4 md:hidden">
                      {filteredWOs.map((wo) => {
                        const skin = woSkin(wo.status);
                        return (
                          <div
                            key={wo.id}
                            data-testid="wo-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => openWorkOrder(wo.id)}
                            onKeyDown={rowKeys(wo.id)}
                            className={`cursor-pointer rounded-fi-panel border border-l-4 border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10 ${skin.rail}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{wo.wo_number}</p>
                              <StatusBadge skin={skin} />
                            </div>
                            <p className="mt-1 text-sm text-white">{wo.asset_name}</p>
                            <p className="text-xs text-fieldiq-muted">{wo.asset_tag} &middot; {wo.assigned_to}</p>
                            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-fieldiq-muted">
                              <span>{wo.opened_date}</span>
                              <span className="font-mono text-sm font-extrabold text-white">{fmtCurrency(wo.total_cost)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </DhPanel>
            </>
          ) : activeView === "schedules" ? (
            <section
              aria-labelledby="schedule-register-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              <header className="border-b border-fieldiq-border px-4 py-4">
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Schedules</p>
                <h2 id="schedule-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                  Preventive maintenance
                </h2>
              </header>
              {schedFailed ? (
                <LoadFailureNotice error={queryFailureError(schedulesQuery)} subject="maintenance schedules" />
              ) : schedLoading ? (
                <div className="space-y-2 p-4" aria-hidden="true">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-10 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                  ))}
                </div>
              ) : schedules && schedules.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">No active maintenance schedules</p>
              ) : (
                <>
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[860px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                          <th className="px-4 py-3">Asset</th>
                          <th className="px-4 py-3">Trigger</th>
                          <th className="px-4 py-3">Every</th>
                          <th className="px-4 py-3">Last performed</th>
                          <th className="px-4 py-3">Next due</th>
                          <th className="px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(schedules ?? []).map((s) => {
                          const skin = scheduleSkin(s);
                          return (
                            <tr key={s.id} className={`border-t border-l-4 border-fieldiq-border ${skin.rail}`}>
                              <td className="px-4 py-3">
                                <p className="text-sm font-bold text-fieldiq-text">{s.asset_name || DASH}</p>
                                <p className="text-[11px] text-fieldiq-muted">{s.asset_number || DASH}</p>
                              </td>
                              <td className="px-4 py-3 text-sm text-fieldiq-text">{(s.trigger_type && TRIGGER_TYPE[s.trigger_type]) || DASH}</td>
                              <td className="px-4 py-3 text-sm tabular-nums text-fieldiq-text">{displayValue(numOrUndefined(s.trigger_value))}</td>
                              <td className="px-4 py-3 text-sm text-fieldiq-muted">{formatInstantDay(s.last_performed_at, timeZone) ?? DASH}</td>
                              <td className="px-4 py-3 text-sm text-fieldiq-text">{formatInstantDay(s.next_due_at, timeZone) ?? DASH}</td>
                              <td className="px-4 py-3"><StatusBadge skin={skin} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="space-y-3 p-4 md:hidden">
                    {(schedules ?? []).map((s) => (
                      <div key={s.id} data-testid="schedule-card" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-extrabold text-fieldiq-text">{s.asset_name || DASH}</p>
                            <p className="text-xs text-fieldiq-muted">{s.asset_number || DASH}</p>
                          </div>
                          <StatusBadge skin={scheduleSkin(s)} />
                        </div>
                        <p className="mt-2 text-sm text-fieldiq-text">
                          Next due {formatInstantDay(s.next_due_at, timeZone) ?? DASH}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                    Active schedules only. &ldquo;Due soon&rdquo; is anything due within 7 days, including past due.
                  </p>
                </>
              )}
            </section>
          ) : (
            <MaintenanceCostReport />
          )}
        </div>
      </section>

      {/* ═══ New Work Order Dialog ═══ */}
      <Dialog open={newWOOpen} onOpenChange={setNewWOOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Work Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Asset</Label>
              <Select value={formAsset} onValueChange={setFormAsset}>
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Select asset" />
                </SelectTrigger>
                <SelectContent>
                  {assetOptions.length > 0 ? (
                    assetOptions.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.tag ? `${a.name} (${a.tag})` : a.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {assetsQuery.isError ? "Couldn't load the rental equipment." : "No rental equipment yet."}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={formPriority} onValueChange={setFormPriority}>
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                className="mt-1"
                rows={3}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Describe the work required..."
              />
            </div>
            <div>
              <Label className="text-xs">Assign To</Label>
              <Select value={formAssignTo} onValueChange={setFormAssignTo}>
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  {/* It used to offer four invented names when the list was empty,
                      and picking one sent a name into a uuid column. */}
                  {crewOptions.length > 0 ? (
                    crewOptions.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {usersQuery.isError ? "Couldn't load the crew list." : "No crew members to assign."}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Checklist Items</Label>
              <div className="space-y-2 mt-1">
                {formChecklist.map((item, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      className="h-9"
                      placeholder={`Step ${i + 1}`}
                      value={item}
                      onChange={(e) => {
                        const next = [...formChecklist];
                        next[i] = e.target.value;
                        setFormChecklist(next);
                      }}
                    />
                    {formChecklist.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 p-0"
                        onClick={() => setFormChecklist(formChecklist.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFormChecklist([...formChecklist, ""])}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Step
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewWOOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWO} disabled={creatingWO}>
              {creatingWO ? "Creating…" : "Create Work Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ New Schedule Dialog ═══ */}
      <Dialog open={newScheduleOpen} onOpenChange={setNewScheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Maintenance Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Asset</Label>
              <Select value={schedAsset} onValueChange={setSchedAsset}>
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Select asset" />
                </SelectTrigger>
                <SelectContent>
                  {assetOptions.length > 0 ? (
                    assetOptions.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.tag ? `${a.name} (${a.tag})` : a.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {assetsQuery.isError ? "Couldn't load the rental equipment." : "No rental equipment yet."}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sched-trigger-type" className="text-xs">Trigger</Label>
              {/* Native and labelled: ui/select prints the raw value in its trigger. */}
              <select
                id="sched-trigger-type"
                value={schedTriggerType}
                onChange={(e) => setSchedTriggerType(e.target.value)}
                className="mt-1 h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
              >
                <option value="">Select trigger</option>
                {SCHEDULE_TRIGGERS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="sched-trigger-value" className="text-xs">
                Every{schedTriggerType ? ` (${SCHEDULE_TRIGGERS.find((t) => t.value === schedTriggerType)?.unit ?? ""})` : ""}
              </Label>
              <Input
                id="sched-trigger-value"
                type="number"
                inputMode="decimal"
                min="0"
                className="h-9 mt-1"
                value={schedTriggerValue}
                onChange={(e) => setSchedTriggerValue(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewScheduleOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSchedule} disabled={creatingSchedule}>
              {creatingSchedule ? "Adding…" : "Add Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceWorkOrders;
